import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Skeleton,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  PictureAsPdf as PdfIcon,
  CreateNewFolder as CreateFolderIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useDropzone } from 'react-dropzone';
import { api, MediaFile, MediaFolder } from '../services/api-client';
import { useAuth } from '../contexts/GoogleAuthContext';

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date
const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get file icon based on type
const getFileIcon = (fileType: string) => {
  switch (fileType) {
    case 'image':
      return <ImageIcon sx={{ color: '#4caf50' }} />;
    case 'video':
      return <VideoIcon sx={{ color: '#f44336' }} />;
    case 'audio':
      return <AudioIcon sx={{ color: '#9c27b0' }} />;
    case 'document':
      return <PdfIcon sx={{ color: '#ff5722' }} />;
    default:
      return <FileIcon sx={{ color: '#607d8b' }} />;
  }
};

export default function MediaLibrary() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [currentPath, setCurrentPath] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'file' | 'folder'; path: string; name: string } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<MediaFile | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; file: MediaFile } | null>(null);

  // Fetch media files
  const { data: mediaData, isLoading, error, refetch } = useQuery({
    queryKey: ['media', currentPath],
    queryFn: () => api.listMediaFiles(currentPath),
  });

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (name: string) => api.createMediaFolder(name, currentPath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setCreateFolderOpen(false);
      setNewFolderName('');
      setSnackbar({ open: true, message: 'Folder created successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to create folder', severity: 'error' });
    },
  });

  // Delete file mutation
  const deleteFileMutation = useMutation({
    mutationFn: (path: string) => api.deleteMediaFile(path),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setSnackbar({ open: true, message: 'File deleted successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to delete file', severity: 'error' });
    },
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: (path: string) => api.deleteMediaFolder(path),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setSnackbar({ open: true, message: `Folder and ${data.deleted_count} items deleted`, severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to delete folder', severity: 'error' });
    },
  });

  // Handle file upload
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const totalFiles = acceptedFiles.length;
    let uploadedCount = 0;

    for (const file of acceptedFiles) {
      try {
        // Get signed URL
        const folder = currentPath || 'General';
        const { upload_url } = await api.getMediaUploadUrl(file.name, file.type, folder);

        // Upload to GCS
        await fetch(upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });

        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      } catch (error: any) {
        console.error('Upload error:', error);
        setSnackbar({ open: true, message: `Failed to upload ${file.name}: ${error.message}`, severity: 'error' });
      }
    }

    setUploading(false);
    setUploadProgress(0);
    queryClient.invalidateQueries({ queryKey: ['media'] });

    if (uploadedCount > 0) {
      setSnackbar({ open: true, message: `${uploadedCount} file(s) uploaded successfully`, severity: 'success' });
    }
  }, [currentPath, queryClient]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  // Navigate to folder
  const navigateToFolder = (path: string) => {
    setCurrentPath(path);
    setSelectedFile(null);
  };

  // Build breadcrumb path
  const pathParts = currentPath ? currentPath.split('/') : [];

  // Copy URL to clipboard
  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'URL copied to clipboard', severity: 'success' });
  };

  // Handle context menu
  const handleContextMenu = (event: React.MouseEvent, file: MediaFile) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      file,
    });
  };

  // Check if user can delete (admin only for folders, developer+ for files)
  const canDeleteFolder = user?.role === 'admin';
  const canDeleteFile = user?.role === 'admin' || user?.role === 'developer';
  const canUpload = user?.role === 'admin' || user?.role === 'developer';

  // Check if current path is protected
  const isProtectedPath = currentPath.startsWith('Onboarding');

  return (
    <Box {...getRootProps()} sx={{ minHeight: '100%', position: 'relative' }}>
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && !isProtectedPath && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed #1976d2',
            borderRadius: 2,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h5" color="primary">
            Drop files here to upload
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <FolderIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">Media Library</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your media assets for embedding across platforms
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value) => value && setViewMode(value)}
          size="small"
        >
          <ToggleButton value="grid">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list">
            <ListViewIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Breadcrumbs */}
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ flex: 1 }}>
            <Link
              component="button"
              variant="body1"
              onClick={() => navigateToFolder('')}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              underline="hover"
            >
              <HomeIcon fontSize="small" />
              Root
            </Link>
            {pathParts.map((part, index) => {
              const path = pathParts.slice(0, index + 1).join('/');
              const isLast = index === pathParts.length - 1;
              return isLast ? (
                <Typography key={path} color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FolderIcon fontSize="small" />
                  {part}
                </Typography>
              ) : (
                <Link
                  key={path}
                  component="button"
                  variant="body1"
                  onClick={() => navigateToFolder(path)}
                  underline="hover"
                >
                  {part}
                </Link>
              );
            })}
          </Breadcrumbs>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {!isProtectedPath && canUpload && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CreateFolderIcon />}
                  onClick={() => setCreateFolderOpen(true)}
                  size="small"
                >
                  New Folder
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => document.querySelector<HTMLInputElement>('input[type="file"]')?.click()}
                  size="small"
                >
                  Upload
                </Button>
              </>
            )}
          </Box>
        </Box>

        {isProtectedPath && (
          <Alert severity="info" sx={{ mt: 2 }} icon={<LockIcon />}>
            This folder is protected. Files can only be modified through the Onboarding Survey interface.
          </Alert>
        )}

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" width={200} height={200} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load media files. Please try again.</Alert>
      ) : (
        <>
          {/* Folders */}
          {mediaData?.folders && mediaData.folders.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Folders ({mediaData.folders.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {mediaData.folders.map((folder) => (
                  <Paper
                    key={folder.path}
                    sx={{
                      p: 2,
                      width: 150,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                    onClick={() => navigateToFolder(folder.path)}
                  >
                    <FolderIcon sx={{ fontSize: 48, color: folder.is_protected ? 'warning.main' : 'primary.main' }} />
                    <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                      {folder.name}
                    </Typography>
                    {folder.is_protected && (
                      <Chip label="Protected" size="small" color="warning" variant="outlined" icon={<LockIcon />} />
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Files */}
          {mediaData?.files && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Files ({mediaData.files.length})
              </Typography>

              {mediaData.files.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {isProtectedPath
                      ? 'No files in this folder'
                      : 'No files yet. Drag and drop files here or click Upload.'}
                  </Typography>
                </Paper>
              ) : viewMode === 'grid' ? (
                // Grid View
                <Grid container spacing={2}>
                  {mediaData.files.map((file) => (
                    <Grid item key={file.path} xs={6} sm={4} md={3} lg={2}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: selectedFile?.path === file.path ? 2 : 0,
                          borderColor: 'primary.main',
                        }}
                        onClick={() => setSelectedFile(file)}
                        onContextMenu={(e) => handleContextMenu(e, file)}
                        onDoubleClick={() => {
                          setPreviewFile(file);
                          setPreviewOpen(true);
                        }}
                      >
                        {file.file_type === 'image' ? (
                          <CardMedia
                            component="img"
                            height="120"
                            image={file.public_url}
                            alt={file.name}
                            sx={{ objectFit: 'cover' }}
                          />
                        ) : file.file_type === 'video' ? (
                          <Box
                            sx={{
                              height: 120,
                              bgcolor: 'grey.200',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <VideoIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 120,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {getFileIcon(file.file_type)}
                          </Box>
                        )}
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Tooltip title={file.name}>
                            <Typography variant="body2" noWrap>
                              {file.name}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ p: 1, pt: 0 }}>
                          <Tooltip title="Copy URL">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(file.public_url); }}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open in new tab">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(file.public_url, '_blank'); }}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!file.is_protected && canDeleteFile && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setItemToDelete({ type: 'file', path: file.path, name: file.name });
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                // Table View
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Modified</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mediaData.files.map((file) => (
                        <TableRow
                          key={file.path}
                          hover
                          selected={selectedFile?.path === file.path}
                          onClick={() => setSelectedFile(file)}
                          onDoubleClick={() => {
                            setPreviewFile(file);
                            setPreviewOpen(true);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getFileIcon(file.file_type)}
                              <Typography variant="body2">{file.name}</Typography>
                              {file.is_protected && <LockIcon fontSize="small" color="warning" />}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={file.file_type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{formatFileSize(file.size)}</TableCell>
                          <TableCell>{formatDate(file.updated)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Copy URL">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(file.public_url); }}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Open">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(file.public_url, '_blank'); }}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!file.is_protected && canDeleteFile && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setItemToDelete({ type: 'file', path: file.path, name: file.name });
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) {
                createFolderMutation.mutate(newFolderName.trim());
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createFolderMutation.mutate(newFolderName.trim())}
            disabled={!newFolderName.trim() || createFolderMutation.isPending}
          >
            {createFolderMutation.isPending ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemToDelete?.type === 'folder' ? 'folder' : 'file'}{' '}
            <strong>{itemToDelete?.name}</strong>?
          </Typography>
          {itemToDelete?.type === 'folder' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will delete all files and subfolders inside this folder.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (itemToDelete?.type === 'folder') {
                deleteFolderMutation.mutate(itemToDelete.path);
              } else if (itemToDelete?.type === 'file') {
                deleteFileMutation.mutate(itemToDelete.path);
              }
            }}
            disabled={deleteFileMutation.isPending || deleteFolderMutation.isPending}
          >
            {(deleteFileMutation.isPending || deleteFolderMutation.isPending) ? (
              <CircularProgress size={20} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{previewFile?.name}</Typography>
            <Box>
              <Tooltip title="Copy URL">
                <IconButton onClick={() => previewFile && copyToClipboard(previewFile.public_url)}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton onClick={() => previewFile && window.open(previewFile.public_url, '_blank')}>
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewFile?.file_type === 'image' && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={previewFile.public_url}
                alt={previewFile.name}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              />
            </Box>
          )}
          {previewFile?.file_type === 'video' && (
            <Box sx={{ textAlign: 'center' }}>
              <video
                src={previewFile.public_url}
                controls
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              />
            </Box>
          )}
          {previewFile?.file_type === 'audio' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <audio src={previewFile.public_url} controls style={{ width: '100%' }} />
            </Box>
          )}
          {!['image', 'video', 'audio'].includes(previewFile?.file_type || '') && (
            <Alert severity="info">
              Preview not available for this file type. Click "Open in new tab" to view.
            </Alert>
          )}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              Public URL (click to copy):
            </Typography>
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', wordBreak: 'break-all', fontFamily: 'monospace' }}
              onClick={() => previewFile && copyToClipboard(previewFile.public_url)}
            >
              {previewFile?.public_url}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          if (contextMenu?.file) {
            setPreviewFile(contextMenu.file);
            setPreviewOpen(true);
          }
          setContextMenu(null);
        }}>
          Preview
        </MenuItem>
        <MenuItem onClick={() => {
          if (contextMenu?.file) copyToClipboard(contextMenu.file.public_url);
          setContextMenu(null);
        }}>
          Copy URL
        </MenuItem>
        <MenuItem onClick={() => {
          if (contextMenu?.file) window.open(contextMenu.file.public_url, '_blank');
          setContextMenu(null);
        }}>
          Open in new tab
        </MenuItem>
        {!contextMenu?.file.is_protected && canDeleteFile && (
          <MenuItem
            onClick={() => {
              if (contextMenu?.file) {
                setItemToDelete({ type: 'file', path: contextMenu.file.path, name: contextMenu.file.name });
                setDeleteConfirmOpen(true);
              }
              setContextMenu(null);
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
